// src/components/dashboard/GhlSyncButton.tsx
// Dashboard-surface GHL sync trigger with a per-org cooldown.
// Shown to owner / admin / trainer / marketing roles.
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";

const COOLDOWN_MS = 5 * 60 * 1000; // 5 minutes
const ALLOWED_ROLES = ["owner", "admin", "trainer", "marketing"];

function cooldownKey(orgId: string) {
  return `ghl_sync_last_${orgId}`;
}

function getLastSynced(orgId: string): number {
  try { return parseInt(localStorage.getItem(cooldownKey(orgId)) ?? "0", 10); }
  catch { return 0; }
}

function setLastSynced(orgId: string) {
  try { localStorage.setItem(cooldownKey(orgId), String(Date.now())); }
  catch { /* ignore */ }
}

export function GhlSyncButton({ orgId, onSynced }: { orgId: string; onSynced?: () => void }) {
  const auth = useAuth();
  const [syncing, setSyncing] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Tick cooldown timer
  useEffect(() => {
    function tick() {
      const elapsed = Date.now() - getLastSynced(orgId);
      const remaining = Math.max(0, COOLDOWN_MS - elapsed);
      setCooldownRemaining(remaining);
    }
    tick();
    const id = setInterval(tick, 5000);
    return () => clearInterval(id);
  }, [orgId]);

  if (!auth.hasRole(...ALLOWED_ROLES)) return null;

  const disabled = syncing || cooldownRemaining > 0;
  const minutesLeft = Math.ceil(cooldownRemaining / 60000);

  async function handleSync() {
    const token = auth.session?.access_token;
    if (!token) { setError("Not signed in."); return; }
    setSyncing(true);
    setResult(null);
    setError(null);
    try {
      const res = await fetch("/api/ghl/sync", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ organizationId: orgId }),
      });
      const json = await res.json() as { synced?: number; connections?: number; error?: string };
      if (!res.ok) throw new Error(json.error ?? `HTTP ${res.status}`);
      setLastSynced(orgId);
      setCooldownRemaining(COOLDOWN_MS);
      setResult(`Sync complete — ${json.synced ?? 0} opportunities across ${json.connections ?? 0} connection${(json.connections ?? 0) !== 1 ? "s" : ""}`);
      onSynced?.();
    } catch (e: unknown) {
      setError((e as Error)?.message ?? "Sync failed");
    } finally {
      setSyncing(false);
    }
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => void handleSync()}
          disabled={disabled}
          title={cooldownRemaining > 0 ? `Available again in ${minutesLeft} min` : "Pull latest opportunity data from GoHighLevel"}
          className={`text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors ${
            disabled
              ? "border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed"
              : "border-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100"
          }`}
        >
          {syncing
            ? "Syncing CRM…"
            : cooldownRemaining > 0
            ? `CRM sync (${minutesLeft}m cooldown)`
            : "Sync CRM data"}
        </button>
        {cooldownRemaining > 0 && !syncing && (
          <div className="h-1 w-20 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-indigo-300 rounded-full transition-all duration-5000"
              style={{ width: `${Math.round((cooldownRemaining / COOLDOWN_MS) * 100)}%` }}
            />
          </div>
        )}
      </div>
      {result && (
        <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5">
          {result}
        </div>
      )}
      {error && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-2.5 py-1.5">
          {error}
        </div>
      )}
    </div>
  );
}
